/**
 * Fuseaux horaires, côté navigateur.
 *
 * ⚠️ **Aucune liste n'est transportée depuis le serveur, et il ne faut pas en
 * ajouter une.** `Intl` porte déjà la base IANA des deux côtés : le client
 * construit son menu avec `Intl.supportedValuesOf`, le serveur valide en
 * demandant à `Intl` s'il sait interpréter la valeur reçue. Une liste figée
 * dans le code serait périmée à la première révision de tzdata, et une liste
 * envoyée à chaque ouverture des Réglages pèserait plus que tout le reste de la
 * réponse.
 *
 * ⚠️ À ne pas confondre avec `lib/dates.ts` et son `browserToday()`. Celui-ci
 * répond « quel jour est-il **ici** », pour l'affichage ; ce fichier-ci sert à
 * dire au serveur où vit l'utilisateur, pour qu'un travail de fond sans
 * navigateur puisse en tenir compte.
 */

/**
 * Fuseau de cet appareil, tel que le système le déclare.
 *
 * `null` si le navigateur ne sait pas le dire — auquel cas l'écran propose le
 * menu complet sans raccourci, plutôt que d'inventer une valeur.
 */
export function deviceTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone !== '' ? zone : null;
  } catch {
    return null;
  }
}

/**
 * Tous les fuseaux connus de cette plateforme, triés.
 *
 * ⚠️ `supportedValuesOf` existe depuis Safari 15.4 ; la cible du projet est
 * iOS 16 (§2), donc il est là. Le repli n'est pas de la superstition pour
 * autant : sans lui, un navigateur plus ancien afficherait un menu **vide**,
 * c'est-à-dire un réglage impossible à corriger sur l'appareil même où il est
 * faux. Le repli garde au moins le fuseau courant et celui de l'appareil.
 */
export function allTimeZones(current: string): string[] {
  let zones: string[];
  try {
    zones = [...(Intl.supportedValuesOf?.('timeZone') ?? [])];
  } catch {
    zones = [];
  }

  const device = deviceTimeZone();
  for (const extra of [current, device]) {
    if (extra !== null && extra !== '' && !zones.includes(extra)) zones.push(extra);
  }
  return zones.sort((a, b) => a.localeCompare(b));
}

/**
 * L'heure qu'il est dans un fuseau donné, `HH:MM`.
 *
 * ⚠️ `hourCycle: 'h23'` explicite, pour la même raison que côté serveur
 * (`services/dayFactory.ts`) : certaines locales rendent minuit « 24:00 ».
 * Ici l'enjeu n'est pas un tri mais la simple cohérence des deux affichages —
 * lire « 24:05 » d'un côté et « 00:05 » de l'autre ferait douter du réglage.
 */
export function timeAt(zone: string, at: Date = new Date()): string | null {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(at);
  } catch {
    return null;
  }
}

/**
 * Écart entre deux fuseaux, en heures, au format « +9 h » / « −2 h 30 ».
 *
 * `null` si l'écart est nul ou incalculable — l'écran n'a alors rien à dire.
 *
 * ⚠️ Calculé en comparant deux formatages du **même instant**, et non par une
 * table d'offsets : c'est la seule méthode qui tienne compte de l'heure d'été,
 * qui ne tombe ni le même jour ni dans le même sens selon l'hémisphère. Entre
 * Paris et Sydney, l'écart réel varie de 8 à 10 heures dans l'année.
 */
export function offsetLabel(from: string, to: string, at: Date = new Date()): string | null {
  const minutesOf = (zone: string): number | null => {
    try {
      // « 2026-08-22, 06:30 » lu comme si c'était de l'UTC : la différence des
      // deux donne exactement le décalage, sans arithmétique de calendrier.
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(at);
      const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
      const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
      return Number.isNaN(asUTC) ? null : asUTC / 60_000;
    } catch {
      return null;
    }
  };

  const a = minutesOf(from);
  const b = minutesOf(to);
  if (a === null || b === null) return null;

  const delta = Math.round(b - a);
  if (delta === 0) return null;

  const sign = delta > 0 ? '+' : '−';
  const abs = Math.abs(delta);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return minutes === 0
    ? `${sign}${String(hours)} h`
    : `${sign}${String(hours)} h ${String(minutes).padStart(2, '0')}`;
}
